<template>
  <div class="vf-p-common-home" data-page-kind="catalog"></div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { onBeforeRouteLeave } from "vue-router";
import { useSeoMeta } from "../../../../composables/useSeoMeta";
import useQuery from "../../../../utils/query";
import useBreadcrumbs from "../../../../utils/breadcrumbs";
import {
  isSeoBridgeRobotsReady,
  requireSeoBridgeRobotsReady,
  withConservativeSeoBridgeRobots,
} from "../../../../utils/seoBridgeSupport";
import {
  restoreScrollWhenReady,
  RESTORE_CATALOG_SCROLL_KEY,
} from "../../../../utils/restoreScrollWhenReady";
const { onLoad } = useBreadcrumbs();
const store = useStore();
const route = useRoute();

// entry-server serializes the verified Home bridge payload and create-app
// restores it before component setup. Reuse that exact payload for the first
// client render so hydration never replaces an indexable SSR head with the
// conservative fallback while a duplicate network request is in flight.
const hydratedHome = !import.meta.env.SSR
  ? store.getters["common/home/get"]
  : null;
const readyHydratedHome =
  hydratedHome?.meta && isSeoBridgeRobotsReady(hydratedHome)
    ? hydratedHome
    : null;
const { meta, applySeo } = useSeoMeta("home", {
  includeCanonical: true,
  robots:
    "noindex, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  initial: readyHydratedHome
    ? {
        title: readyHydratedHome.meta.title,
        description: readyHydratedHome.meta.description,
        robots: readyHydratedHome.robots,
      }
    : undefined,
});
const error = computed(() => store.getters["vuefront/error"]);
const { query } = useQuery();

const applyHomeSeo = (home: any) => {
  store.commit("common/home/setHome", home);
  meta.title = home.meta.title;
  meta.description = home.meta.description;
  if (home?.meta?.keyword && String(home.meta.keyword).trim()) {
    meta.keyword = String(home.meta.keyword).trim();
  } else if ("keyword" in meta) {
    delete (meta as any).keyword;
  }
  applySeo({
    title: meta.title,
    description: meta.description,
    robots: home.robots,
  });
  onLoad([]);
};

const handleLoadData = async () => {
  if (readyHydratedHome) {
    applyHomeSeo(readyHydratedHome);
    return;
  }

  const response = await store.dispatch("apollo/query", {
    query,
    throwOnError: import.meta.env.SSR,
  });
  if (!error.value) {
    const rawHome = response?.home || store.getters["apollo/get"]?.home;
    if (!rawHome?.meta) {
      if (import.meta.env.SSR) {
        throw new Error("Home API returned an invalid response");
      }
      return;
    }
    if (import.meta.env.SSR) {
      requireSeoBridgeRobotsReady(rawHome, "Home");
    }
    const home = isSeoBridgeRobotsReady(rawHome)
      ? rawHome
      : withConservativeSeoBridgeRobots(rawHome);

    applyHomeSeo(home);
  }
};
await handleLoadData();

const BACK_NAV_KEY = "vf_back";

// Home extensions render below this shell after mount, so restore only once
// the browser can actually land on the saved Y position.
onMounted(() => {
  if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
    return;
  }

  const cameBack = sessionStorage.getItem(BACK_NAV_KEY) === "1";
  if (cameBack) {
    const expectedHomePath = window.location.pathname + window.location.search;

    setTimeout(() => {
      sessionStorage.removeItem(BACK_NAV_KEY);
    }, 200);

    const savedScrollY = Number(
      sessionStorage.getItem(RESTORE_CATALOG_SCROLL_KEY) || 0
    );
    if (savedScrollY > 0) {
      restoreScrollWhenReady(savedScrollY, {
        expectedPath: expectedHomePath,
      });
    }
  }
});

// Save state before leaving, same as catalog pages.
onBeforeRouteLeave(() => {
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem("vf_last_catalog_url", route.fullPath);
    sessionStorage.setItem(
      RESTORE_CATALOG_SCROLL_KEY,
      String(window.scrollY || 0)
    );
  }
});
</script>
<graphql>
{
  home {
    h1
    robots
    robotsReady
    dateModified
    meta {
      title
      description
      keyword
    }
  }
}
</graphql>
